import * as Yup from 'yup';

import {
  SORTBY_OPTIONS,
  mobileDevices,
  mobileGamingGenres,
  mobilePeripherals,
  mobilePlayFrequencies,
  mobilePlayLocations,
  mobileSessionPlaytimes
} from '../../utils/constants';
import { useEffect, useState } from 'react';

import SelectQuestion from '../SelectQuestion/SelectQuestion';
import YesNoButton from '../YesNoButton/YesNoButton';
import { createLocalizedOptions } from '../../pages/root/App';
import iconSmartphone from '../../assets/images/ubisoft-playtest-icon-smartphone-black.webp';
import messages from './messages';
import siteMessages from '../../pages/root/siteMessages';
import { useIntl } from 'react-intl';

interface Props {
  isInterested: boolean | null;
  values: any;
  errors: any;
  setFieldValue: any;
  updateSchema: (value: any) => void;
  withHeader?: boolean;
  isSurvey?: boolean;
}

const MobileTesting = ({
  isInterested,
  values,
  errors,
  setFieldValue,
  updateSchema,
  withHeader = true,
  isSurvey = false
}: Props) => {
  const intl = useIntl();
  const [interested, setInterested] = useState(isInterested);

  const playFrequenciesOptions = createLocalizedOptions(
    mobilePlayFrequencies,
    messages
  );
  const sessionPlaytimesOptions = createLocalizedOptions(
    mobileSessionPlaytimes,
    messages,
    SORTBY_OPTIONS.Id,
    false
  );
  const playLocationsOptions = createLocalizedOptions(
    mobilePlayLocations,
    messages
  );
  const gamingGenresOptions = createLocalizedOptions(
    mobileGamingGenres,
    messages,
    SORTBY_OPTIONS.Name
  );
  const devicesOptions = createLocalizedOptions(mobileDevices, messages);

  const peripheralsOptions = createLocalizedOptions(
    mobilePeripherals,
    messages,
    SORTBY_OPTIONS.Name
  );

  useEffect(() => {
    const mobileTestingSchema = {
      playFrequencyId: Yup.number().required(
        intl.formatMessage({ ...siteMessages.FIELD_REQUIRED })
      ),
      sessionPlaytimeId: Yup.number().required(
        intl.formatMessage({ ...siteMessages.FIELD_REQUIRED })
      ),
      playLocationIds: Yup.array(Yup.number())
        .min(1, intl.formatMessage({ ...siteMessages.FIELD_REQUIRED }))
        .required(intl.formatMessage({ ...siteMessages.FIELD_REQUIRED })),
      gamingGenreIds: Yup.array(Yup.number())
        .min(1, intl.formatMessage({ ...siteMessages.FIELD_REQUIRED }))
        .max(
          5,
          intl.formatMessage({ ...siteMessages.TOO_MANY_VALUES }, { len: 5 })
        )
        .required(intl.formatMessage({ ...siteMessages.FIELD_REQUIRED })),
      deviceIds: Yup.array(Yup.number())
        .min(1, intl.formatMessage({ ...siteMessages.FIELD_REQUIRED }))
        .required(intl.formatMessage({ ...siteMessages.FIELD_REQUIRED })),
      peripheralIds: Yup.array(Yup.number())
        .min(1, intl.formatMessage({ ...siteMessages.FIELD_REQUIRED }))
        .required(intl.formatMessage({ ...siteMessages.FIELD_REQUIRED }))
    };
    updateSchema(mobileTestingSchema);
  }, []);

  return (
    <div className="mobile-testing">
      {withHeader && (
        <div className="header">
          <img src={iconSmartphone} alt="" className="mt-n1" />
          <h3>{intl.formatMessage({ ...messages.headerMobileTesting })}</h3>
        </div>
      )}
      {isSurvey && (
        <div className="mt-3">
          <YesNoButton
            id="candidateMobile"
            name="mobileTesting"
            label={intl.formatMessage({ ...messages.mobileTesting })}
            value={interested}
            onChange={(value: boolean) => {
              setInterested(value);
              setFieldValue('mobileTesting', value);
            }}
          />
        </div>
      )}
      <div className="content">
        {!isSurvey && (
          <YesNoButton
            id="candidateMobile"
            name="mobileTesting"
            label={intl.formatMessage({ ...messages.mobileTesting })}
            value={isInterested}
            onChange={(value: boolean) => {
              setInterested(value);
              setFieldValue('mobileTesting', value);
            }}
          />
        )}
        {interested && (
          <>
            <SelectQuestion
              prefix="candidateMobile"
              controlId="playFrequencyId"
              label={intl.formatMessage({ ...messages.playFrequencies })}
              isMulti={false}
              options={playFrequenciesOptions}
              value={values.playFrequencyId ?? []}
              error={
                typeof errors == 'string' ? errors : errors?.playFrequencyId
              }
              setFieldValue={setFieldValue}
            />
            <SelectQuestion
              prefix="candidateMobile"
              controlId="sessionPlaytimeId"
              label={intl.formatMessage({ ...messages.sessionPlaytimes })}
              isMulti={false}
              options={sessionPlaytimesOptions}
              value={values.sessionPlaytimeId ?? []}
              error={
                typeof errors == 'string' ? errors : errors?.sessionPlaytimeId
              }
              setFieldValue={setFieldValue}
            />
            <SelectQuestion
              prefix="candidateMobile"
              controlId="playLocationIds"
              label={intl.formatMessage({ ...messages.playLocations })}
              isMulti={true}
              options={playLocationsOptions}
              value={values.playLocationIds ?? []}
              error={
                typeof errors == 'string' ? errors : errors?.playLocationIds
              }
              setFieldValue={setFieldValue}
            />
            <SelectQuestion
              prefix="candidateMobile"
              controlId="gamingGenreIds"
              label={intl.formatMessage({ ...messages.gamingGenres })}
              isMulti={true}
              options={gamingGenresOptions}
              value={values.gamingGenreIds ?? []}
              error={
                typeof errors == 'string' ? errors : errors?.gamingGenreIds
              }
              setFieldValue={setFieldValue}
            />
            <SelectQuestion
              prefix="candidateMobile"
              controlId="deviceIds"
              label={intl.formatMessage({ ...messages.devices })}
              isMulti={true}
              options={devicesOptions}
              value={values.deviceIds ?? []}
              error={typeof errors == 'string' ? errors : errors?.deviceIds}
              setFieldValue={setFieldValue}
            />
            <SelectQuestion
              prefix="candidateMobile"
              controlId="peripheralIds"
              label={intl.formatMessage({ ...messages.peripherals })}
              isMulti={true}
              options={peripheralsOptions}
              value={values.peripheralIds ?? []}
              error={typeof errors == 'string' ? errors : errors?.peripheralIds}
              setFieldValue={setFieldValue}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default MobileTesting;
